import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../../lib/auth/axiosApi';

const KIND_LABEL = { money: 'Money', match: 'Match', chouette: 'Chouette' };

function TableRow({ t, onOpen, cta }) {
  const scores = Object.values(t.scores || {});
  return (
    <div className={`bg-row ${t.your_turn ? 'bg-row-turn' : ''}`} onClick={() => onOpen(t.id)}>
      <span className="bg-row-kind">{KIND_LABEL[t.kind]}{t.kind === 'match' ? ` to ${t.match_length}` : ''}</span>
      <span className="bg-row-players">{t.players.join(t.kind === 'chouette' ? ', ' : ' vs ')}</span>
      <span className="bg-row-meta">
        {t.status === 'waiting' ? 'waiting' : t.status === 'finished' ? 'done' : `game ${t.game_no || 1}`}
        {t.kind !== 'chouette' && t.status !== 'waiting' && scores.length === 2 ? ` · ${scores.join('-')}` : ''}
        {t.is_private ? ' · invite' : ''}
      </span>
      {t.your_turn && <span className="bg-badge">your turn</span>}
      {cta && <button className="bg-btn bg-btn-primary" onClick={(e) => { e.stopPropagation(); cta.onClick(t.id); }}>{cta.label}</button>}
    </div>
  );
}

export default function Lobby({ onOpenTable }) {
  const [lobby, setLobby] = useState(null);
  const [kind, setKind] = useState('match');
  const [opponent, setOpponent] = useState('bot');
  const [matchLength, setMatchLength] = useState(5);
  const [jacoby, setJacoby] = useState(true);
  const [beaver, setBeaver] = useState(false);
  const [clock, setClock] = useState('none');
  const [stake, setStake] = useState('');
  const [bots, setBots] = useState(2);
  const [chouetteMode, setChouetteMode] = useState('relaxed');
  const [blunders, setBlunders] = useState(null);
  const [creating, setCreating] = useState(false);
  const isLoggedIn = (() => { try { return !!localStorage.getItem('access_token'); } catch (e) { return false; } })();
  const [tab, setTab] = useState('play');

  const load = useCallback(async () => {
    try {
      const res = await axiosInstance.get('backgammon/');
      setLobby(res.data);
    } catch (err) {
      if (err.response && err.response.status === 403) setLobby({ forbidden: true });
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (tab === 'blunders' && blunders === null && isLoggedIn) {
      axiosInstance.get('backgammon/blunders/?limit=100')
        .then((res) => setBlunders(res.data || []))
        .catch(() => setBlunders([]));
    }
  }, [tab, blunders, isLoggedIn]);

  const create = async () => {
    setCreating(true);
    const options = { jacoby, beaver };
    if (kind === 'match') options.match_length = matchLength;
    if ((kind === 'match' || kind === 'chouette') && Number(stake) > 0) options.stake = Number(stake);
    if (clock !== 'none' && kind !== 'chouette') {
      const [delay, reserve] = clock.split('/').map(Number);
      options.clock_delay = delay;
      options.clock_reserve = reserve;
    }
    if (kind === 'chouette') options.chouette_mode = chouetteMode;
    const body = { kind, options };
    if (kind === 'chouette') body.bots = bots;
    else if (opponent === 'bot') body.opponent = 'bot';
    else if (opponent !== 'open') body.opponent = Number(opponent);
    try {
      const res = await axiosInstance.post('backgammon/', body);
      onOpenTable(res.data.id);
    } catch (err) {
      toast.error((err.response && err.response.data && err.response.data.error) || 'could not create table');
    } finally {
      setCreating(false);
    }
  };

  const join = async (id) => {
    if (!isLoggedIn) { window.location.href = '/login'; return; }
    try {
      await axiosInstance.post(`backgammon/${id}/join/`);
      onOpenTable(id);
    } catch (err) {
      toast.error((err.response && err.response.data && err.response.data.error) || 'could not join');
    }
  };

  if (!lobby) return <div className="bg-loading">loading lobby…</div>;
  if (lobby.forbidden) return <div className="bg-loading">no backgammon access on this account</div>;

  const others = lobby.players.filter((p) => !p.is_you);
  const yourTurn = lobby.mine.filter((t) => t.your_turn).length;

  return (
    <div className="bg-lobby">
      <div className="bg-lobby-tabs">
        {['play', 'leaderboard', 'blunders', 'rules'].map((t) => (
          <button key={t} className={`bg-tab ${tab === t ? 'bg-tab-on' : ''}`} onClick={() => setTab(t)}>
            {t}{t === 'play' && yourTurn ? ` (${yourTurn})` : ''}
          </button>
        ))}
      </div>

      {tab === 'play' && (
        <div className="bg-lobby-grid">
          <div className="bg-panel bg-create">
            <div className="bg-panel-title">New table</div>
            {!isLoggedIn ? (
              <div className="bg-signin-cta">
                <p>Anyone can watch. <b>Sign in to play</b> — challenge a friend, join an open table, or take on the bot.</p>
                <a className="bg-btn bg-btn-primary bg-btn-wide" href="/login">Sign in to play</a>
                <a className="bg-link" href="/signup" style={{ float: 'none', display: 'inline-block', marginTop: 8 }}>or create an account</a>
              </div>
            ) : (<>
            <div className="bg-seg">
              {['match', 'money', 'chouette'].map((k) => (
                <button key={k} className={`bg-seg-btn ${kind === k ? 'on' : ''}`} onClick={() => setKind(k)}>{KIND_LABEL[k]}</button>
              ))}
            </div>
            {kind === 'match' && (
              <label className="bg-field">Match length
                <select value={matchLength} onChange={(e) => setMatchLength(Number(e.target.value))}>
                  {[1, 3, 5, 7, 9, 11, 13, 15, 17, 21, 25].map((n) => <option key={n} value={n}>{n} point{n > 1 ? 's' : ''}</option>)}
                </select>
              </label>
            )}
            {(kind === 'match' || kind === 'chouette') && (
              <label className="bg-field">{kind === 'chouette' ? 'Stake — $ per point (optional)' : 'Stake — winner takes ($, optional)'}
                <input type="number" min="0" step="1" inputMode="decimal" placeholder="friendly (no money)"
                  value={stake} onChange={(e) => setStake(e.target.value)} />
                {Number(stake) > 0 && <span className="bg-hint">settles to the Ledger when the {kind === 'chouette' ? 'chouette ends (per point, netted between players)' : 'match ends'} — approved by both automatically</span>}
              </label>
            )}
            {kind !== 'chouette' ? (
              <label className="bg-field">Opponent
                <select value={opponent} onChange={(e) => setOpponent(e.target.value)}>
                  <option value="bot">🤖 bot</option>
                  <option value="open">open table (anyone joins)</option>
                  {others.map((p) => <option key={p.id} value={p.id}>invite {p.username}{p.online ? ' 🟢' : ''}</option>)}
                </select>
              </label>
            ) : (
              <label className="bg-field">Bots to fill seats
                <select value={bots} onChange={(e) => setBots(Number(e.target.value))}>
                  {[0, 1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </label>
            )}
            {kind === 'chouette' && (
              <label className="bg-field">Cube mode
                <select value={chouetteMode} onChange={(e) => setChouetteMode(e.target.value)}>
                  <option value="relaxed">Relaxed — only doublers act, rest auto-pass</option>
                  <option value="strict">Strict — every teammate must click roll</option>
                </select>
              </label>
            )}
            {kind !== 'match' && (
              <label className="bg-check"><input type="checkbox" checked={jacoby} onChange={(e) => setJacoby(e.target.checked)} /> Jacoby rule</label>
            )}
            {kind === 'money' && (
              <label className="bg-check"><input type="checkbox" checked={beaver} onChange={(e) => setBeaver(e.target.checked)} /> Beavers</label>
            )}
            {kind !== 'chouette' && (
              <label className="bg-field">Clock
                <select value={clock} onChange={(e) => setClock(e.target.value)}>
                  <option value="none">no clock</option>
                  <option value="12/120">12s delay + 2 min</option>
                  <option value="12/300">12s delay + 5 min</option>
                  <option value="8/60">blitz: 8s + 1 min</option>
                  <option value="20/600">relaxed: 20s + 10 min</option>
                </select>
              </label>
            )}
            <button className="bg-btn bg-btn-primary bg-btn-wide" onClick={create} disabled={creating}>
              {creating ? 'setting up…' : kind === 'chouette' ? 'Open chouette' : opponent === 'bot' ? 'Play the bot' : opponent === 'open' ? 'Open table' : 'Send challenge'}
            </button>
            {kind === 'chouette' && <div className="bg-hint">3+ players (bots count). Friends join from the lobby, you hit start.</div>}
            </>)}
          </div>

          <div className="bg-lobby-lists">
            <div className="bg-panel">
              <div className="bg-panel-title">Your tables</div>
              {lobby.mine.length ? lobby.mine.map((t) => <TableRow key={t.id} t={t} onOpen={onOpenTable} />)
                : <div className="bg-hint">nothing going. start something.</div>}
            </div>
            <div className="bg-panel">
              <div className="bg-panel-title">Open tables & challenges</div>
              {lobby.open.length ? lobby.open.map((t) => (
                <TableRow key={t.id} t={t} onOpen={onOpenTable} cta={{ label: t.kind === 'chouette' && t.status === 'playing' ? 'Join next game' : 'Join', onClick: join }} />
              )) : <div className="bg-hint">no open tables</div>}
            </div>
            {lobby.live.length > 0 && (
              <div className="bg-panel">
                <div className="bg-panel-title">Watch live</div>
                {lobby.live.map((t) => (
                  <TableRow key={t.id} t={t} onOpen={onOpenTable}
                    cta={t.kind === 'chouette' ? { label: 'Join', onClick: join } : null} />
                ))}
              </div>
            )}
            {lobby.recent.length > 0 && (
              <div className="bg-panel">
                <div className="bg-panel-title">Recent</div>
                {lobby.recent.map((t) => <TableRow key={t.id} t={t} onOpen={onOpenTable} />)}
              </div>
            )}
            <div className="bg-panel">
              <div className="bg-panel-title">Online</div>
              <div className="bg-online">
                {lobby.players.filter((p) => p.online).map((p) => <span key={p.id} className="bg-online-chip">🟢 {p.username}</span>)}
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'leaderboard' && (
        <div className="bg-panel">
          <table className="bg-leader">
            <thead>
              <tr><th>player</th><th>rating</th><th>matches</th><th>won</th><th>games</th><th>money</th><th>chouette</th></tr>
            </thead>
            <tbody>
              {lobby.players.map((p) => (
                <tr key={p.id} className={p.is_you ? 'bg-leader-you' : ''}>
                  <td>{p.online ? '🟢 ' : ''}{p.username}</td>
                  <td>{p.rating.toFixed(1)}</td>
                  <td>{p.matches_played}</td>
                  <td>{p.matches_won}</td>
                  <td>{p.games_played}</td>
                  <td className={p.money_net > 0 ? 'pos' : p.money_net < 0 ? 'neg' : ''}>{p.money_net > 0 ? '+' : ''}{p.money_net}</td>
                  <td className={p.chouette_net > 0 ? 'pos' : p.chouette_net < 0 ? 'neg' : ''}>{p.chouette_net > 0 ? '+' : ''}{p.chouette_net}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="bg-hint">Rating is FIBS-style, human-vs-human matches only. Money and chouette columns are running point totals.</div>
        </div>
      )}

      {tab === 'blunders' && (
        <div className="bg-panel">
          {!isLoggedIn && <div className="bg-hint">Sign in to track and review your blunders across matches. <a className="bg-link" href="/login" style={{ float: 'none' }}>sign in</a></div>}
          {isLoggedIn && blunders === null && <div className="bg-hint">crunching your games…</div>}
          {blunders && blunders.length === 0 && (
            <div className="bg-hint">No blunders yet — play a few games and the plays that gave up the most equity show up here.</div>
          )}
          {blunders && blunders.length > 0 && (
            <table className="bg-leader">
              <thead><tr><th>dice</th><th>you played</th><th>best was</th><th>lost</th><th>type</th></tr></thead>
              <tbody>
                {blunders.map((b) => (
                  <tr key={b.id}>
                    <td>{b.dice[0]}-{b.dice[1]}</td>
                    <td>{b.played}</td>
                    <td>{b.best}</td>
                    <td className="neg">−{(b.loss * 100).toFixed(1)}%</td>
                    <td className={`bg-an-${b.category}`}>{b.category}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="bg-hint">Your worst checker plays across all matches, worst first (heuristic — same engine as post-game analysis).</div>
        </div>
      )}

      {tab === 'rules' && <Rules />}
    </div>
  );
}

function Rules() {
  return (
    <div className="bg-panel bg-rules">
      <h3>How this table plays</h3>
      <p><b>Moves.</b> Opening roll is one die each (ties re-roll); the higher die moves first using both. You must use both dice if any legal play does; if only one can be played you must play the larger when possible. Doubles play four times. Checkers on the bar must enter before anything else moves. You bear off only once all fifteen are home; a die higher than your highest point bears off from the highest point.</p>
      <p><b>Scoring.</b> Single = 1, gammon (loser bore nothing off) = 2, backgammon (gammon and loser still on the bar or in the winner's home board) = 3, times the cube.</p>
      <p><b>Cube.</b> Double before rolling on your turn when the cube is centered or yours. Take and own the cube at 2x, or drop and lose the current value. Money games can use the Jacoby rule (gammons only count once the cube has been turned) and beavers (take and immediately redouble, keeping the cube).</p>
      <p><b>Match play.</b> First to N points. The game right after someone first reaches N-1 is the Crawford game — no doubling. After that (post-Crawford) the cube is back. Gammons always count. Clocks: per-move delay then a reserve for the whole match; run out and you lose the match.</p>
      <p><b>Chouette.</b> One player — the box — plays against everyone else. The captain rolls and moves for the team; teammates can talk it over in team chat. Every team member has their own cube against the box. On the team's turn each member decides whether to double their cube (the captain's roll waits up to 15s for them); the box takes or drops each one separately. When the box doubles, it doubles every cube it can, and each member takes or drops on their own. Dropped cubes settle on the spot; if the captain is out, the next live teammate takes over. At the end every live cube settles at its value. If the box wins, the box stays and the captain goes to the back of the line; if the team wins, the captain becomes the new box and the old box goes to the back.</p>
      <p><b>Resigning.</b> On your turn, offer to resign a single, gammon or backgammon; your opponent can accept or play on.</p>
    </div>
  );
}
